// Remove the direct import of axios
// import axios from "axios";

// Import the configured axios instance instead
import { instance } from "./index";
import {
    DEFAULT_NOTE_EXPIRY,
    NOTE_ATTACHMENTS_FIELD,
    NOTE_ATTACHMENT_REMOVAL_LIST_FIELD,
    NOTE_DOMAIN,
} from "../constants/noteAttachment.constants";

const NOTE_ENDPOINT = "/api/notes/note";
const NOTE_TITLE_QUERY_KEY = "title";

const normalizeNoteQueryTitle = (title) => String(title).trim();

const buildNoteTitleQuery = (title) =>
    new URLSearchParams({
        [NOTE_TITLE_QUERY_KEY]: normalizeNoteQueryTitle(title),
    }).toString();

/**
 * Updates a note by sending a PUT request.
 * The payload will be automatically encrypted by the axios interceptor.
 */
export const updateNote = async (data) => {
    try {
        // Use the 'instance' for the request.
        // We no longer need to set headers or the full URL here.
        const response = await instance.put(NOTE_ENDPOINT, data);
        return response.data;
    } catch (error) {
        throw error;
    }
};

const appendFormValue = (formData, key, value) => {
    if (value === undefined || value === null) return;

    formData.append(key, value);
};

const buildNoteFormData = ({ title, content, expiry, attachments, removeAttachmentBlobNames }) => {
    const formData = new FormData();

    appendFormValue(formData, "title", title);
    appendFormValue(formData, "content", content);
    appendFormValue(formData, "domain", NOTE_DOMAIN);
    appendFormValue(formData, "expiry", expiry || DEFAULT_NOTE_EXPIRY);
    attachments.forEach((attachmentFile) => {
        formData.append(NOTE_ATTACHMENTS_FIELD, attachmentFile);
    });
    removeAttachmentBlobNames.forEach((blobName) => {
        formData.append(NOTE_ATTACHMENT_REMOVAL_LIST_FIELD, blobName);
    });

    return formData;
};

export const updateNoteWithAttachments = async (noteData) => {
    try {
        const formData = buildNoteFormData(noteData);
        const response = await instance.put(NOTE_ENDPOINT, formData);
        return response.data;
    } catch (error) {
        throw error;
    }
};

const buildCloudNotePayload = ({ noteData, removeAttachmentBlobNames }) => {
    if (!removeAttachmentBlobNames.length) return noteData;

    return {
        ...noteData,
        [NOTE_ATTACHMENT_REMOVAL_LIST_FIELD]: removeAttachmentBlobNames,
    };
};

/**
 * @param {{
 *   attachments?: File[],
 *   noteData: { title: string, content: string, expiry: string },
 *   removeAttachmentBlobNames?: string[]
 * }} update
 * @returns {Promise<object>} Server response body.
 */
export const updateCloudNote = async ({
    attachments = [],
    noteData,
    removeAttachmentBlobNames = [],
}) => {
    if (attachments.length) {
        return updateNoteWithAttachments({
            ...noteData,
            attachments,
            removeAttachmentBlobNames,
        });
    }

    return updateNote(
        buildCloudNotePayload({
            noteData,
            removeAttachmentBlobNames,
        })
    );
};

/**
 * Gets a note. This is a GET request, so it will not be encrypted.
 *
 * @param {string} title - Note title to fetch.
 * @returns {Promise<object>} Fetched note data.
 */
export const getNote = async (title) => {
    try {
        const params = buildNoteTitleQuery(title);
        const response = await instance.get(`${NOTE_ENDPOINT}?${params}`);
        return response.data.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Deletes a note. This is a DELETE request, so it will not be encrypted.
 */
export const deleteNote = async (title) => {
    try {
        const params = buildNoteTitleQuery(title);
        await instance.delete(`${NOTE_ENDPOINT}?${params}`);
    } catch (error) {
        throw error;
    }
};
